import React, { useMemo, useState } from "react";
import { Search, X, ChevronDown } from "lucide-react";
import { ACTIVITY_CATEGORIES, ALL_ACTIVITIES, searchActivities } from "../lib/interests";

const chipCls = (active) =>
    `px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
        active
            ? "bg-ember-orange text-white"
            : "bg-snowcap text-granite border border-ridge-line hover:border-ember-orange"
    }`;

/**
 * Single-select activity picker, searchable + grouped by category.
 * Sourced from the shared taxonomy in lib/interests.js so it stays in sync
 * with the Profile / Onboarding interests. `value`/`onChange` are slug-based.
 */
export default function ActivityPicker({ value, onChange, testid = "activity-picker" }) {
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(null); // category key, "__none", or null (auto)

    const matches = useMemo(() => (q.trim() ? searchActivities(q) : null), [q]);
    const selected = value ? ALL_ACTIVITIES.find((a) => a.slug === value) : null;

    const pick = (slug) => { onChange(slug); setQ(""); };

    return (
        <div data-testid={testid} className="space-y-3">
            {selected && (
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-ember-orange text-white text-xs font-bold uppercase tracking-wider">
                        {selected.label}
                        <button
                            type="button"
                            data-testid={`${testid}-clear`}
                            onClick={() => onChange("")}
                            className="hover:opacity-80"
                            aria-label="Clear activity"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </span>
                    <span className="text-xs text-slate-gray">{selected.category}</span>
                </div>
            )}

            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                <input
                    data-testid={`${testid}-search`}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search activities — e.g. fly fishing, snowshoeing, bouldering…"
                    className="w-full pl-10 pr-3 py-2.5 rounded-md bg-snowcap border border-ridge-line text-sm focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20"
                />
            </div>

            {matches ? (
                matches.length ? (
                    <div className="flex flex-wrap gap-2">
                        {matches.map((a) => (
                            <button
                                key={a.slug}
                                type="button"
                                data-testid={`${testid}-opt-${a.slug}`}
                                onClick={() => pick(a.slug)}
                                className={chipCls(value === a.slug)}
                            >
                                {a.label}
                                <span className="opacity-50 normal-case font-medium"> · {a.category}</span>
                            </button>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-slate-gray">No activity matches “{q}”. Pick the closest one below.</p>
                )
            ) : (
                <div className="space-y-1.5">
                    {ACTIVITY_CATEGORIES.map((cat) => {
                        const Icon = cat.icon;
                        const isOpen =
                            open === cat.key ||
                            (open === null && selected && selected.categoryKey === cat.key);
                        return (
                            <div key={cat.key} className="rounded-lg border border-ridge-line bg-snowcap overflow-hidden">
                                <button
                                    type="button"
                                    data-testid={`${testid}-cat-${cat.key}`}
                                    onClick={() => setOpen(isOpen ? "__none" : cat.key)}
                                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
                                >
                                    <Icon className="w-4 h-4 text-midnight-pine shrink-0" />
                                    <span className="text-sm font-bold text-midnight-pine flex-1">{cat.label}</span>
                                    <ChevronDown className={`w-4 h-4 text-slate-gray transition-transform ${isOpen ? "rotate-180" : ""}`} />
                                </button>
                                {isOpen && (
                                    <div className="px-3 pb-3 flex flex-wrap gap-2">
                                        {cat.subs.map((s) => (
                                            <button
                                                key={s.slug}
                                                type="button"
                                                data-testid={`${testid}-opt-${s.slug}`}
                                                onClick={() => pick(s.slug)}
                                                className={chipCls(value === s.slug)}
                                            >
                                                {s.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
